import { AppContext } from "App";
import { StringComparisonTypes } from "Configuration";

import { CaseInsensitiveMap } from "../../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { IAttrStringValue } from "../../../../../Global/Base/DataTypes/IAttrValue";
import { EditableControl } from "../../../../UI.BaseControls/EditableControl";
import { FilterDatetypeOperationMapper } from "./FilterDatetypeOperationMapper";
import {
    FilterBlockAttrNameToData,
    FilterConvertFunction,
    FilterEmptyType,
    FilterJsonResultSign,
    FilterJsonResultSpan,
    IFieldRegistrator,
    IFilterFieldInfo,
    IFilterFieldInfoAdditional,
    IFilterJsonDataAttr,
    IFilterJsonResultValue,
    IFilterServerOperation,
    IFilterServerOperationInput,
    IFilterServerValueOperPrepare,
    IFilterValue,
    IMultiOneOperation,
    IOperation,
} from "./FilterInterfaces";
import { logger } from "./logger";

export class FilterRegistrations {
    public static readonly numericTypes = ["int64", "integer", "double", "decimal", "money", "short", "single", "bcd"];
    public static readonly stringTypes = ["string", "strnum", "text"];

    private static _struct: CaseInsensitiveMap<IFilterValue> = new CaseInsensitiveMap<IFilterValue>();
    private static _structDefaultOperation: CaseInsensitiveMap<IOperation> = new CaseInsensitiveMap<IOperation>();
    private static _structDefMultiOper: CaseInsensitiveMap<IMultiOneOperation> = new CaseInsensitiveMap<IMultiOneOperation>();
    private static _fieldTypeRegistrator: CaseInsensitiveMap<IFieldRegistrator> = new CaseInsensitiveMap<IFieldRegistrator>();
    private static _structDataTypeServerOper: CaseInsensitiveMap<{ [operId: string]: IFilterServerOperation }> = new CaseInsensitiveMap<{ [operId: string]: IFilterServerOperation }>();

    public static registerDataTypeFilter(value: IFilterValue): void {
        //Structure.struct[value.dataType.toLowerCase()] = value;
        FilterRegistrations._struct.set(value.dataType, value);
        for (const gl of value.groupList) {
            for (const op of gl.operationList) {
                FilterDatetypeOperationMapper.register(value.dataType, op.id, op.fieldList);
            }
        }
    }

    /**
     * Zaregistruje strukturu filtru
     * @param value Struktura filtru
     * @param dataTypes Seznam datovych typu, pro kazdy je struktura zklonovana
     */
    public static registerMultiTypeFilter(value: IFilterValue, dataTypes?: string[]): void {
        if (dataTypes == undefined)
            FilterRegistrations.registerDataTypeFilter(value);
        else {
            const stringify = JSON.stringify(value);
            for (const type of dataTypes) {
                const obj = JSON.parse(stringify) as IFilterValue;
                obj.dataType = type;
                //Structure.struct[type.toLowerCase()] = obj;
                FilterRegistrations._struct.set(type, obj);

                for (const gl of value.groupList) {
                    for (const op of gl.operationList) {
                        FilterDatetypeOperationMapper.register(obj.dataType, op.id, op.fieldList);
                    }
                }
            }
        }
    }

    /**
     * Vrati strukturu filtru pro dany datovy typ
     * @param dataType
     */
    public static getDataTypeFilter(attrData: FilterBlockAttrNameToData): IFilterValue {
        const struct = FilterRegistrations._struct.get(attrData?.dataType);

        const NULL_FILTERING = ["is_empty", "is_not_empty"];
        const COMPARISON_FILTERING = ["greater_than", "greater_or_equal_than", "less_than", "less_or_equal_than"];
        const EQUALITY_FILTERING = ["is", "not_is", "value_is", "value_not_is", "is_equal", "not_is_equal"];
        const LIKE_FILTERING = ["start_with", "not_start_with", "end_with", "not_end_with", "contain", "not_contain"];

        if (struct)
            for (const group of struct.groupList) {
                for (const operation of group.operationList) {
                    if (NULL_FILTERING.includes(operation.id))
                        operation.isVisible = !!attrData.canFilterNull;
                    if (COMPARISON_FILTERING.includes(operation.id))
                        operation.isVisible = !!attrData.canFilterCompare;
                    if (EQUALITY_FILTERING.includes(operation.id))
                        operation.isVisible = !!attrData.canFilterEqual;
                    if (LIKE_FILTERING.includes(operation.id)) // enum je specialita na klientovi, řídit like filtry podle equal
                        operation.isVisible = struct.dataType === "enum" ? !!attrData.canFilterEqual : !!attrData.canFilterLike;
                }
            }

        return struct;
    }

    /**
     * Vrátí výchozí operaci pro dany datový typ
     * @param dataType Název datového typu
     * @param preferedOperation (Nepovinný) Název preferované operace. Má přednost před výchozí
     */
    public static getDefaultOperationByDataType(dataType: string, preferedOperation?: string): IOperation {
        if (FilterRegistrations._structDefaultOperation.has(dataType))
            return FilterRegistrations._structDefaultOperation.get(dataType);
        if (!FilterRegistrations._struct.has(dataType))
            return undefined;

        const dataTypeInfo = FilterRegistrations._struct.get(dataType);
        let findFirst = false;
        let firstOper: IOperation = undefined;
        let defaultOperationFound: IOperation = undefined;

        for (const grp of dataTypeInfo.groupList) {
            for (const operation of grp.operationList) {
                if (!operation.isVisible)
                    continue;
                if (!findFirst) {
                    findFirst = true;
                    firstOper = operation;
                }
                if (operation.isDefault) {
                    FilterRegistrations._structDefaultOperation.set(dataType, operation);
                    defaultOperationFound = operation;
                }
                if (operation.id === preferedOperation) {
                    FilterRegistrations._structDefaultOperation.set(dataType, operation);
                    return operation;
                }
            }
        }

        if (defaultOperationFound)
            return defaultOperationFound;

        FilterRegistrations._structDefaultOperation.set(dataType, firstOper);
        return firstOper;
    }

    /**
     * Vratí operaci pro dany datovy typ na základe id operace
     * @param dataType
     * @param operId
     */
    public static getOperationByOperationId(dataType: string, operId: string): IOperation {
        if (!FilterRegistrations._struct.has(dataType))
            return undefined;

        const dtInfo = FilterRegistrations._struct.get(dataType);
        const firstOper: IOperation = undefined;

        for (const grp of dtInfo.groupList) {
            for (const oper of grp.operationList) {
                if (!oper.isVisible)
                    continue;

                if (oper.id == operId)
                    return oper;
            }
        }
        FilterRegistrations._structDefaultOperation.set(dataType, firstOper);
        return firstOper;
    }

    public static getMultiOperationByOperationId(dataType: string, operId: string): IMultiOneOperation {
        if (!FilterRegistrations._struct.has(dataType))
            return undefined;

        const dtInfo = FilterRegistrations._struct.get(dataType);
        const firstOper: IMultiOneOperation = undefined;

        if (!dtInfo.multiOperation)
            return undefined;

        for (const grp of dtInfo.multiOperation.groupList) {
            for (const oper of grp.operationList) {
                if (!oper.isVisible)
                    continue;

                if (oper.id == operId)
                    return oper;
            }
        }
        FilterRegistrations._structDefMultiOper.set(dataType, firstOper);
        return firstOper;
    }

    /**
     * Pomocná funkce, která vrátí filtrovací strukturu a pokud obsahuje vice fieldu je to plněno polem z values postupně jak jdou po sobě
     * @param dataType Datový typ filtru tak jak je ve struktuře
     * @param operation Operace filtru tak jak je ve strukuře
     * @param values Hodnota, která má být použitá, může obsahovat i pole hodnot.
     */
    public static createValuesFromData(dataType: string, operation: string, values: any): { [id: string]: IFilterJsonResultValue } {
        const operationStruct = FilterRegistrations.getOperationByOperationId(dataType, operation);

        const returnData: { [id: string]: IFilterJsonResultValue } = {};
        if (!Array.isArray(values)) values = [ values ];

        let i = 0;
        for (const field of operationStruct.fieldList) {
            if (i >= values.length) break;
            returnData[field.id] = {
                value: values[i++],
                fieldId: field.id,
                fieldType: field.fieldType,
            };
        }
        return returnData;
    }

    /**
     * Zaregistruje pole pro generovani fieldu
     * @param fieldType
     * @param control
     * @param fromJson
     * @param toJson
     */
    public static registerField(fieldType: string, control: any, afterCreateMethod?: (control: any) => void, from?: (v: string, type: FilterConvertFunction) => any, to?: (v: any, type: FilterConvertFunction) => string): void {
        const def_from = (v: string, _type: FilterConvertFunction): any => {
            return v;
        };
        const def_to = (v: string, _type: FilterConvertFunction): string => {
            return v;
        };
        const f: IFieldRegistrator = {
            control: control,
            from: from || def_from,
            to: to || def_to,
            afterCreate: afterCreateMethod,
        };
        FilterRegistrations._fieldTypeRegistrator.set(fieldType, f);
    }

    public static getFieldInstance(fieldType: string): EditableControl {
        const field = FilterRegistrations._fieldTypeRegistrator.get(fieldType);
        if (field == null)
            return null;

        const instanceOfControl = new field.control;
        if (field.afterCreate && typeof field.afterCreate === "function") {
            field.afterCreate(instanceOfControl);
        }
        return instanceOfControl;
    }

    public static getField(fieldType: string): IFieldRegistrator {
        return FilterRegistrations._fieldTypeRegistrator.get(fieldType);
    }

    public static fieldTo(type: FilterConvertFunction, fieldType: string, value: any): string {
        return FilterRegistrations._fieldTypeRegistrator.get(fieldType).to(value, type);
    }

    public static fieldFrom(type: FilterConvertFunction, fieldType: string, value: string): any {
        return FilterRegistrations._fieldTypeRegistrator.get(fieldType).from(value, type);
    }

    /**
     * Zaregistruje serverove operace
     * @param dType
     * @param operations
     */
    public static registerServerOperation(dType: string | string[], operations: { [operId: string]: IFilterServerOperation }): void {
        let dTypes: string[] = [];
        if (typeof dType === "string") {
            dTypes = [dType];
        } else
            dTypes = dType;

        for (const dataType of dTypes) {
            const opers: { [operId: string]: IFilterServerOperation } = FilterRegistrations._structDataTypeServerOper.has(dataType) ? FilterRegistrations._structDataTypeServerOper.get(dataType) : {};

            const keys = Object.keys(operations);
            for (const key of keys) {
                opers[key] = operations[key];
            }

            FilterRegistrations._structDataTypeServerOper.set(dataType, opers);
        }
    }

    private static _getValuesArray(input: Array<IFilterJsonResultValue>): Array<{ id: string; value: any; [val: string]: string }> {
        const a: Array<{ id: string; value: string; [val: string]: string }> = [];

        for (const key of input) {
            a.push({
                id: key.fieldId,
                value: key.value,
            });
        }

        return a;
    }

    /**
     * Rozparsuje serverovou podmínku a přidá ji do seznamu použitých podmínek
     * @param servValCond Serverová podmínka, do která je vložena hodnota
     * @param attrName Název atributu
     * @param value Vkládaná hodnota
     * @param orderCondition Pole jednotlivých podmínek, které budou předány na server
     */
    private static _parseServerCondition(servValCond: IFilterServerOperationInput, usedCond: string, attrName: string, value: any, orderCondition: Array<IFilterServerValueOperPrepare>, span: string): void {
        const condition = FilterRegistrations.updateCondition(usedCond, { "attrName": attrName, "value": value, "span": span }, servValCond.escapeValue);

        orderCondition.push({ condition: condition, sign: servValCond.sign || "or" });

        if (servValCond.conditionalOperation) {
            for (const conditionalOperation of servValCond.conditionalOperation) {
                if (conditionalOperation && conditionalOperation.containsValue) {
                    const values = conditionalOperation.separator ? value.split(conditionalOperation.separator) : value;

                    if (values.includes(conditionalOperation.containsValue)) {
                        const conditionalCondition = FilterRegistrations.updateCondition(conditionalOperation.condition, { "attrName": attrName, "value": value });

                        if (conditionalOperation.isForReplace) {
                            orderCondition.splice(-1, 1);
                        }

                        orderCondition.push({ condition: conditionalCondition, sign: conditionalOperation.sign || "or" });
                    }
                }
            }
        }
    }

    /**
     * Vrátí podminku ze struktury upravenou pro odeslani na server
     * @param attrNameToDataType Funkce,ktera na zaklade AttrName vrati datovy typ
     * @param data Strukutra z filtrovaciho pole
     * @return Vraci string ze serverovou podminkou
     */
    public static getServerCondition(attrNameToDataType: (v: string) => string, data: IFilterJsonDataAttr[]): string {
        let condition = "";

        for (const d of data) {
            if (!d.enabled)
                continue;

            const attrName = d.attrName;
            const dataType = attrNameToDataType(d.attrName);

            let resAppend = false;
            let subCond = "";
            let subCondSign = "";
            let fullChar = 0;
            for (const res of d.conditions) {
                if (!res.enabled || !res.isValid)
                    continue;
                let condInfo: IFilterServerOperation = FilterRegistrations._getServerOper(dataType, res.operation);
                if (condInfo && condInfo.subOperation) {
                    for (const subCondKey in condInfo.subOperation) {
                        if (res.subOperation == subCondKey)
                            condInfo = condInfo.subOperation[subCondKey];
                    }
                }
                if (condInfo == undefined) {
                    logger.warn(`Server operation for datatype '${dataType}' and operation '${res.operation}' not found`);
                    continue;
                }

                if (resAppend && subCond.length > 0)
                    subCondSign = FilterRegistrations._getSignString(res.operator);
                else
                    subCondSign = "";
                resAppend = true;

                // pridani spojky mezi dva vyrazy
                if (subCond) {
                    if (subCondSign)
                        subCond += subCondSign;
                    else
                        subCond += " AND ";
                }
                subCond += " (";

                fullChar = subCond.length; // ulozeni soucasnych znaku podminky
                let skipBeginCondition: boolean = true;//informace o tom jestli se beginCondition použije ve výsledné podmínce filtru
                if (condInfo.beginCondition) {
                    //funkce odstraní zkrácené/nezkrácené hodnoty podle nastavení useForShortenedValues
                    const getFilteredValueFunction = (v: any): any => {
                        if (condInfo.useForShortenedValues === undefined || (condInfo.useForShortenedValues === (v instanceof IAttrStringValue && v.shortened))) {
                            if (v instanceof IAttrStringValue)
                                return v.valueOf();
                            else
                                return v;
                        } else
                            return undefined;
                    };
                    const valuesToUse: Array<IFilterJsonResultValue> = [];
                    for (const resVal of res.values) {
                        const valueToUse = Object.assign({}, resVal);
                        if (resVal.value && !Array.isArray(resVal.value)) {
                            valueToUse.value = getFilteredValueFunction(resVal.value);
                        } else if (Array.isArray(resVal.value)) {
                            valueToUse.value = [];
                            for (const v of resVal.value) {
                                const newVal = getFilteredValueFunction(v);

                                if (newVal)
                                    valueToUse.value.push(newVal);
                            }
                        }
                        valuesToUse.push(valueToUse);
                    }
                    //Podmínka se aplikuje, pokud po filtrování nějaké hodnoty zůstaly.
                    skipBeginCondition = !condInfo.noBeginValueParameters && !valuesToUse.some((vtu) => (vtu.value && !Array.isArray(vtu.value)) || ((Array.isArray(vtu.value) && vtu.value.length > 0)));
                    if (!skipBeginCondition) {
                        subCond += FilterRegistrations.updateCondition(condInfo.beginCondition, {
                            "attrName": attrName,
                            "value": FilterRegistrations._getValuesArray(valuesToUse),
                        });
                    }
                }

                if (condInfo.emptyFilter === FilterEmptyType.empty) {
                    subCond += FilterRegistrations._createEmptyOrFilledCondition(attrName, true);
                } else if (condInfo.emptyFilter === FilterEmptyType.notEmpty) {
                    subCond += FilterRegistrations._createEmptyOrFilledCondition(attrName, false);
                }

                const orderCondition: Array<IFilterServerValueOperPrepare> = [];
                let servValCond: IFilterServerOperationInput;

                for (const value of res.values) {
                    // pokud je hodnota prazdna podminka se nevyhodnocuje
                    if (!value.value)
                        continue;

                    servValCond = condInfo.inputs ? condInfo.inputs[value.fieldId] : undefined;

                    if (servValCond == undefined) {
                        //logger.warn(`Server operation for datatype '${dataType}' and operation '${res.operationId}' and fieldId '${value.fieldId}' not found`);
                        continue;
                    }
                    const span = this._getSpanString(servValCond.span, d.displayFormat);

                    // kontrola na pouziti additional podminky
                    let skip = false;
                    if (value.additional && servValCond.additional) {
                        const addKeys = Object.keys(value.additional);
                        for (const addKey of addKeys) {
                            if (servValCond.additional[addKey]) {
                                skip = true;
                                const useAdd = addKey == "including" && !value.additional.including ? false : true;
                                const cond = useAdd ? servValCond.additional[addKey] : servValCond.condition;

                                FilterRegistrations._parseServerCondition(servValCond, cond, attrName, FilterRegistrations.fieldTo(FilterConvertFunction.sql, value.fieldType, value.value), orderCondition, span);
                            }
                        }
                    }

                    if (skip)
                        continue; // pokud je pouzita podminka s additional zbytek se nevyhodnocuje

                    if (Array.isArray(value.value)) {
                        for (let v of value.value) {
                            let skipValue: boolean;
                            if (v instanceof IAttrStringValue) {
                                skipValue = !(servValCond.useForShortenedValues === undefined || v.shortened === servValCond.useForShortenedValues);
                                v = v.valueOf();
                            } else {
                                skipValue = !(servValCond.useForShortenedValues === undefined || servValCond.useForShortenedValues === false);
                            }
                            if (!skipValue)
                                FilterRegistrations._parseServerCondition(servValCond, servValCond.condition, attrName, FilterRegistrations.fieldTo(FilterConvertFunction.sql, value.fieldType, v), orderCondition, span);
                        }
                    } else
                        FilterRegistrations._parseServerCondition(servValCond, servValCond.condition, attrName, FilterRegistrations.fieldTo(FilterConvertFunction.sql, value.fieldType, value.value), orderCondition, span);
                }
                //Pokud byla použita beginCondition a zároveň se aplikuje inputCondition, vloží se mezi ně patřičný operátor.
                if (!skipBeginCondition && orderCondition.length > 0)
                    subCond += ` ${servValCond.signForBeginCondition ?? "AND"} `;

                subCond += FilterRegistrations._finallyValuesCond(orderCondition);

                if (condInfo.endCondition) {
                    if (subCond)
                        subCond += " AND ";
                    subCond += FilterRegistrations.updateCondition(condInfo.endCondition, { "attrName": attrName, "value": FilterRegistrations._getValuesArray(res.values) });
                }
                if (subCond.length != fullChar
                )
                    subCond += ") "; // pokud se pocet znaku zmenil, exituje podminka, pokud ne, zrusime prvni zavorku podminka byla prazdna
                else {
                    const remCnt = fullChar > 2 ? 6 : 2; // pokud uz podminka predtim nejaka byla, tedy je delsi nez 2, je potreba odstranit i operator
                    subCond = subCond.substring(0, fullChar - remCnt);
                }
            }

            if (subCond.length > 0) {
                if (condition.length > 0)
                    condition += FilterRegistrations._getSignString(d.operator);
                condition += `(${subCond})`; //`
            }
        }

        return condition;
    }

    /**
     * Vytváří podmínku pro Vyplněno/Nevyplněno
     * @param attrName Atribut
     * @param isEmpty Zda se jedná o filtrování prázdné hodnoty
     */
    private static _createEmptyOrFilledCondition(attrName: string, isEmpty: boolean): string {
        const hasLinkage = attrName.includes(".");
        let condition = "";

        if (hasLinkage) {
            condition += FilterRegistrations.updateCondition(`{attrName} ${ isEmpty ? "=" : "<>" } NULL`, {
                "attrName": attrName.slice(0, attrName.lastIndexOf(".")),

            });

            condition += isEmpty ? " or " : " and ";
        }

        condition += FilterRegistrations.updateCondition(`{attrName} ${ isEmpty ? "=" : "<>" } NULL`, {
            "attrName": attrName,
        });

        return condition;
    }

    private static _getSignString(sign: FilterJsonResultSign): string {
        return sign ? " " + sign : undefined;
    }

    private static _getSpanString(span: FilterJsonResultSpan, displayFormat: string): string {
        if (!displayFormat || displayFormat === "t" || displayFormat === "f" || displayFormat === "g")
            return "minute";

        return span;
    }

    /**
     * Upravi podmínku, nahradi parametry
     * @param condition podminka
     * @param values seznam parametru s novyma hodnotama
     * @param escapeValues Zda má dojít k escapu nežádoucí znaků
     */
    public static updateCondition(condition: string, values: { [rep: string]: string | Array<{ id: string; value: any; [val: string]: string }> } = {}, escapeValues: boolean = true): string {
        const keys = Object.keys(values);
        let cond = condition;

        for (const key of keys) {
            let val = values[key];
            const replaceArray: Array<{ key: string; value: string }> = [];
            //#region Uprava hodnoty value
            if (key == "value" && val) {
                if (Array.isArray(val)) {
                    for (const v of values[key] as Array<{ id: string; value: any; [val: string]: string }>) {
                        if (v.value instanceof Array) {
                            let nVal = "";
                            for (const vValue of v.value) {
                                if (nVal.length > 0) nVal += ", ";
                                nVal += `'${vValue}'`;//`
                            }
                            v.value = nVal;
                        } else
                            v.value = escapeValues ? FilterRegistrations._escapeConditionValue(v.value) : v.value;

                        replaceArray.push({ key: `${key}:${v.id}`, value: v.value }); //`
                    }
                } else {
                    val = escapeValues ? FilterRegistrations._escapeConditionValue(val) : val;
                    replaceArray.push({ key: key, value: val as string });
                }
            } else {
                replaceArray.push({ key: key, value: val as string });
            }
            //#endregion Uprava hodnoty value

            for (const rp of replaceArray) {
                const regExp = new RegExp("{" + rp.key + "}", "igm");
                cond = cond.replace(regExp, rp.value);
            }
        }

        return cond;
    }

    /**
     * Provede escape nežádoucích znaků
     * @param value Vstup, který má být upraven
     */
    private static _escapeConditionValue(value: string): string {
        let newVal = "";
        // uprava hodnoty na spravne nahrazeni hvezdicky
        for (let i = 0, lng = value.length; i < lng; i++) {
            let ch = value[i];
            if (ch == "\\") {
                const chnext = value?.[i + 1];
                if (chnext == "*" || chnext == "?") {
                    i++;
                    newVal += chnext;
                    continue;
                } else {
                    newVal += ch; // escapování samotného \
                }
            } else if (ch == "%" || ch == "_") {
                newVal += "\\";
            } else if (ch == "'") {
                newVal += "'";
            } else if (ch == "?")
                ch = "_";
            else if (ch == "*")
                ch = "%";

            newVal += ch;
        }
        return newVal;
    }

    private static _finallyValuesCond(values: Array<IFilterServerValueOperPrepare> = []): string {
        let cond = "";

        for (const value of values) {
            if (!value)
                continue;
            if (cond && cond.length > 0)
                cond += " " + FilterRegistrations._getSignString(value.sign);
            cond += " " + value.condition;
        }

        return cond;
    }

    /**
     * Vrati operaci serveru
     * @param dataType
     * @param id
     */
    private static _getServerOper(dataType: string, id: string): IFilterServerOperation {
        if (!FilterRegistrations._structDataTypeServerOper.has(dataType))
            return undefined;

        const operByDtype = FilterRegistrations._structDataTypeServerOper.get(dataType);
        const keys = Object.keys(operByDtype);
        for (const key of keys) {
            if (key == id)
                return operByDtype[key];
        }

        return undefined;
    }

    /**
     * Vrátí defaultní operaci filtrování pro daný datový typ
     * @param dataType Datový typ
     */
    public static getDefaultOperation(dataType: string): IOperation {
        let preferedOperation: string | null = undefined;
        if (["string", "enum", "text", "strnum"].includes(dataType)) {
            if (AppContext.instance.configuration.defaultFilter === StringComparisonTypes.startsWith)
                preferedOperation = "start_with";
            else if (AppContext.instance.configuration.defaultFilter === StringComparisonTypes.contains)
                preferedOperation = "contain";
        }

        return FilterRegistrations.getDefaultOperationByDataType(dataType, preferedOperation);
    }

    public static getIOperation(id: string, locKey: string, fieldList: IFilterFieldInfo[] = [], order: number = 99, isDefault: boolean = false, isVisible: boolean = true, captLocKey?: string): IOperation {
        return {
            id: id,
            subId: null,
            lockey: locKey,
            fieldList: fieldList,
            order: order,
            isDefault: isDefault,
            isVisible: isVisible,
            captionLocKey: captLocKey,
        };
    }

    public static getIMultiOperation(id: string, lockey: string, value?: any, captionHTML: string = "", order: number = 99, isDefault: boolean = false, isVisible: boolean = true): IMultiOneOperation {
        return {
            id: id,
            lockey: lockey,
            value: value || "nope",
            order: order,
            isDefault: isDefault,
            isVisible: isVisible,
            captionHtml: captionHTML,
        };
    }

    public static getIFilterFieldInfo(id: string, fieldType: string, additional?: IFilterFieldInfoAdditional, fromJsonFormatter?: (value: string) => string): IFilterFieldInfo {
        return {
            id: id,
            fieldType: fieldType,
            additional: additional,
            fromJsonFormatter: fromJsonFormatter,
        };
    }
}
